import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  Plus, 
  Search, 
  Users, 
  Building2, 
  Phone, 
  Mail, 
  Zap,
  TrendingUp,
  MapPin,
  Euro,
  Eye,
  Filter,
  Grid3X3,
  List,
  Calendar,
  User,
  AlertCircle,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from "lucide-react";
import { toast } from "sonner";
import ClientForm from "./ClientForm";
import ClientDetails from "./ClientDetails";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  
  // New states for filtering and view modes
  const [filters, setFilters] = useState({
    status: "all",
    supplier: "all",
    dateRange: "all",
    commercial: "all",
    solvability: "all"
  });
  const [viewMode, setViewMode] = useState("grid"); // "grid", "list", "compact"
  const [showFilters, setShowFilters] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc"); // "asc", "desc", "none"

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log("Clients with creator info loaded:", response.data); // Debug
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Erreur lors du chargement des clients");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClient = async (clientData, documents) => {
    try {
      // Create client
      const response = await axios.post(`${API}/clients`, clientData);
      const newClient = response.data;
      
      // Upload documents if any
      if (documents && Object.values(documents).some(doc => doc)) {
        for (const [docType, file] of Object.entries(documents)) {
          if (file) {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('file_type', docType);
            
            await axios.post(`${API}/clients/${newClient.id}/documents`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          }
        }
      }
      
      toast.success("Client ajouté avec succès");
      setShowAddDialog(false);
      fetchClients();
    } catch (error) {
      console.error("Error creating client:", error);
      toast.error("Erreur lors de la création du client");
    }
  };

  const handleViewClient = (client) => {
    setSelectedClient(client);
    setShowDetailsDialog(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      nouveau: "bg-blue-100 text-blue-800",
      en_cours: "bg-amber-100 text-amber-800",
      signe: "bg-emerald-100 text-emerald-800",
      refuse: "bg-red-100 text-red-800",
      attente: "bg-purple-100 text-purple-800"
    };
    return colors[status] || colors.nouveau;
  };

  const getStatusLabel = (status) => {
    const labels = {
      nouveau: "Nouveau",
      en_cours: "En cours",
      signe: "Signé", 
      refuse: "Refusé",
      attente: "En attente"
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateRelative = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now - date;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return "Hier";
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} semaines`;
    if (diffDays < 365) return `Il y a ${Math.floor(diffDays / 30)} mois`;
    return `Il y a ${Math.floor(diffDays / 365)} ans`;
  };

  const clearFilters = () => {
    setFilters({
      status: "all",
      supplier: "all",
      dateRange: "all",
      commercial: "all",
      solvability: "all"
    });
  };

  const getUniqueValues = (field) => {
    const values = clients.map(client => {
      switch(field) {
        case 'supplier':
          return [client.fournisseur_elec_actuel, client.fournisseur_gaz_actuel].filter(Boolean);
        case 'commercial':
          return client.assigned_commercial;
        case 'solvability':
          return client.note_solvabilite;
        default:
          return null;
      }
    }).flat().filter(Boolean);
    
    return [...new Set(values)];
  };

  const filteredClients = clients.filter(client => {
    // Search term filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || (
      client.nom?.toLowerCase().includes(searchLower) ||
      client.prenom?.toLowerCase().includes(searchLower) ||
      client.company_name?.toLowerCase().includes(searchLower) ||
      client.email?.toLowerCase().includes(searchLower) ||
      client.siren?.toLowerCase().includes(searchLower) ||
      client.ville?.toLowerCase().includes(searchLower)
    );

    // Status filter
    const matchesStatus = !filters.status || filters.status === "all" || client.etat_dossier === filters.status;

    // Supplier filter
    const matchesSupplier = !filters.supplier || filters.supplier === "all" ||
      client.fournisseur_elec_actuel === filters.supplier ||
      client.fournisseur_gaz_actuel === filters.supplier;

    // Solvability filter
    const matchesSolvability = !filters.solvability || filters.solvability === "all" || client.note_solvabilite === filters.solvability;

    // Commercial/Assignation filter
    const matchesCommercial = !filters.commercial || filters.commercial === "all" || client.assigned_commercial === filters.commercial;

    // Date range filter
    let matchesDateRange = true;
    if (filters.dateRange && filters.dateRange !== "all" && client.created_at) {
      const clientDate = new Date(client.created_at);
      const now = new Date();
      const diffDays = Math.ceil((now - clientDate) / (1000 * 60 * 60 * 24));
      
      switch(filters.dateRange) {
        case 'today':
          matchesDateRange = diffDays === 0;
          break;
        case 'week':
          matchesDateRange = diffDays <= 7;
          break;
        case 'month':
          matchesDateRange = diffDays <= 30;
          break;
        case 'quarter':
          matchesDateRange = diffDays <= 90;
          break;
        case 'year':
          matchesDateRange = diffDays <= 365;
          break;
      }
    }

    return matchesSearch && matchesStatus && matchesSupplier && matchesSolvability && matchesCommercial && matchesDateRange;
  });

  // Sort filtered clients by creation date
  const sortedClients = [...filteredClients].sort((a, b) => {
    if (sortOrder === "none") return 0;
    
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    
    if (sortOrder === "asc") {
      return dateA - dateB; // Plus ancien d'abord
    } else {
      return dateB - dateA; // Plus récent d'abord
    }
  });

  const toggleSortOrder = () => {
    if (sortOrder === "desc") {
      setSortOrder("asc");
    } else if (sortOrder === "asc") {
      setSortOrder("none");
    } else {
      setSortOrder("desc");
    }
  };

  const getSortIcon = () => {
    if (sortOrder === "asc") return <ArrowUp className="w-4 h-4" />;
    if (sortOrder === "desc") return <ArrowDown className="w-4 h-4" />;
    return <ArrowUpDown className="w-4 h-4" />;
  };

  const getSortLabel = () => {
    if (sortOrder === "asc") return "Plus ancien d'abord";
    if (sortOrder === "desc") return "Plus récent d'abord";
    return "Tri par date";
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="space-y-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-6 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestion Clients</h1>
          <p className="text-gray-600 mt-1">Gérez votre portefeuille de clients énergétiques</p>
        </div>
        
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-client-button">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Ajouter un nouveau client</DialogTitle>
              <DialogDescription>
                Renseignez les informations complètes du client
              </DialogDescription>
            </DialogHeader>
            
            <ClientForm
              onSave={handleSaveClient}
              onCancel={() => setShowAddDialog(false)}
              isEditing={false}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="flex items-center space-x-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Rechercher un client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-clients"
            />
          </div>

          {/* Filter Toggle */}
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? "bg-blue-50 border-blue-200" : ""}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtres
          </Button>

          {/* Sort Toggle */}
          <Button 
            variant="outline" 
            onClick={toggleSortOrder}
            className={sortOrder !== "none" ? "bg-purple-50 border-purple-200" : ""}
            title={getSortLabel()}
          >
            {getSortIcon()}
            <span className="ml-2 hidden sm:inline">{getSortLabel()}</span>
          </Button>

          {/* View Mode Toggle */}
          <div className="flex border rounded-lg">
            <Button 
              variant={viewMode === "grid" ? "default" : "ghost"} 
              size="sm"
              onClick={() => setViewMode("grid")}
              className="rounded-r-none"
            >
              <Grid3X3 className="w-4 h-4" />
            </Button>
            <Button 
              variant={viewMode === "list" ? "default" : "ghost"} 
              size="sm"
              onClick={() => setViewMode("list")}
              className="rounded-none"
            >
              <List className="w-4 h-4" />
            </Button>
            <Button 
              variant={viewMode === "compact" ? "default" : "ghost"} 
              size="sm"
              onClick={() => setViewMode("compact")}
              className="rounded-l-none"
            >
              <Users className="w-4 h-4" />
            </Button>
          </div>

          <div className="text-sm text-gray-500">
            {sortedClients.length} client(s) trouvé(s)
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Filtres avancés</h3>
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Effacer
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {/* Status Filter */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">État</label>
                <Select value={filters.status} onValueChange={(value) => setFilters({...filters, status: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les états" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les états</SelectItem>
                    <SelectItem value="nouveau">Nouveau</SelectItem>
                    <SelectItem value="en_cours">En cours</SelectItem>
                    <SelectItem value="signe">Signé</SelectItem>
                    <SelectItem value="refuse">Refusé</SelectItem>
                    <SelectItem value="attente">En attente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Date Range Filter */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Date création</label>
                <Select value={filters.dateRange} onValueChange={(value) => setFilters({...filters, dateRange: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Toutes les dates" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les dates</SelectItem>
                    <SelectItem value="today">Aujourd'hui</SelectItem>
                    <SelectItem value="week">Cette semaine</SelectItem>
                    <SelectItem value="month">Ce mois</SelectItem>
                    <SelectItem value="quarter">Ce trimestre</SelectItem>
                    <SelectItem value="year">Cette année</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Supplier Filter */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Fournisseur</label>
                <Select value={filters.supplier} onValueChange={(value) => setFilters({...filters, supplier: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous fournisseurs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous fournisseurs</SelectItem>
                    {getUniqueValues('supplier').map(supplier => (
                      <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Solvability Filter */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Solvabilité</label>
                <Select value={filters.solvability} onValueChange={(value) => setFilters({...filters, solvability: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Toutes notes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes notes</SelectItem>
                    <SelectItem value="excellent">Excellent</SelectItem>
                    <SelectItem value="bon">Bon</SelectItem>
                    <SelectItem value="moyen">Moyen</SelectItem>
                    <SelectItem value="faible">Faible</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Commercial/Assignation Filter */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Assignation</label>
                <Select value={filters.commercial} onValueChange={(value) => setFilters({...filters, commercial: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les commerciaux" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les commerciaux</SelectItem>
                    {getUniqueValues('assigned_commercial').map(commercial => (
                      <SelectItem key={commercial} value={commercial}>{commercial}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Active Filters Count */}
              <div className="flex items-end">
                <div className="text-sm text-gray-600">
                  {Object.values(filters).filter(value => value !== "all" && value !== "").length > 0 && (
                    <div className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                      {Object.values(filters).filter(value => value !== "all" && value !== "").length} filtre(s) actif(s)
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Clients Display */}
      {viewMode === "grid" && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {sortedClients.map((client) => (
            <Card key={client.id} className="hover-lift cursor-pointer" data-testid={`client-card-${client.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg font-semibold text-gray-900 mb-1">
                      {client.company_name}
                    </CardTitle>
                    <CardDescription className="text-sm text-gray-600">
                      {client.prenom} {client.nom}
                    </CardDescription>
                    <div className="flex items-center space-x-1 text-xs text-gray-500 mt-1">
                      <Calendar className="w-3 h-3" />
                      <span>Créé {formatDateRelative(client.created_at)}</span>
                      {client.created_by_name && (
                        <>
                          <span>•</span>
                          <User className="w-3 h-3" />
                          <span>par {client.created_by_name}</span>
                        </>
                      )}
                      {client.telepro_name && (
                        <>
                          <span>•</span>
                          <User className="w-3 h-3 text-orange-500" />
                          <span className="text-orange-600">Telepro: {client.telepro_name}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className={getStatusColor(client.etat_dossier)}>
                      {getStatusLabel(client.etat_dossier)}
                    </Badge>
                    <div className="text-xs text-gray-500 mt-1">
                      {formatDate(client.created_at)}
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Contact Info */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Mail className="w-4 h-4" />
                    <span className="truncate">{client.email}</span>
                  </div>
                  {client.telephone_portable && (
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Phone className="w-4 h-4" />
                      <span>{client.telephone_portable}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-2 text-gray-600">
                    <MapPin className="w-4 h-4" />
                    <span className="truncate">{client.ville || client.adresse}</span>
                  </div>
                </div>

                {/* Energy Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-1 text-gray-500">
                      <Zap className="w-3 h-3" />
                      <span className="text-xs">Puissance</span>
                    </div>
                    <div className="font-medium">{client.puissance_souscrite} kVA</div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center space-x-1 text-gray-500">
                      <Building2 className="w-3 h-3" />
                      <span className="text-xs">SIREN</span>
                    </div>
                    <div className="font-medium">{client.siren || "N/A"}</div>
                  </div>
                </div>

                {/* Suppliers */}
                {(client.fournisseur_elec_actuel || client.fournisseur_gaz_actuel) && (
                  <div className="text-sm">
                    <div className="text-gray-500 text-xs mb-1">Fournisseurs actuels</div>
                    <div className="space-y-1">
                      {client.fournisseur_elec_actuel && (
                        <div className="text-gray-700">⚡ {client.fournisseur_elec_actuel}</div>
                      )}
                      {client.fournisseur_gaz_actuel && (
                        <div className="text-gray-700">🔥 {client.fournisseur_gaz_actuel}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Commission */}
                {client.commission && (
                  <div className="flex items-center space-x-2 text-sm bg-emerald-50 p-2 rounded">
                    <Euro className="w-4 h-4 text-emerald-600" />
                    <span className="text-emerald-700 font-medium">
                      Commission: {client.commission}€
                    </span>
                  </div>
                )}

                {/* Action Button */}
                <Button 
                  onClick={() => handleViewClient(client)}
                  className="w-full mt-4"
                  variant="outline"
                  data-testid={`view-client-${client.id}`}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Voir détails
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === "list" && (
        <div className="space-y-4">
          {sortedClients.map((client) => (
            <Card key={client.id} className="hover-lift cursor-pointer" data-testid={`client-card-${client.id}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-6 gap-4 items-center">
                    {/* Company & Person */}
                    <div className="md:col-span-2">
                      <div className="font-semibold text-gray-900">{client.company_name}</div>
                      <div className="text-sm text-gray-600">{client.prenom} {client.nom}</div>
                      <div className="flex items-center space-x-1 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDateRelative(client.created_at)}</span>
                        {client.created_by_name && (
                          <>
                            <span>•</span>
                            <User className="w-3 h-3" />
                            <span>par {client.created_by_name}</span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Contact */}
                    <div>
                      <div className="text-sm text-gray-600">{client.email}</div>
                      <div className="text-sm text-gray-600">{client.telephone_portable}</div>
                      <div className="text-sm text-gray-500">{client.ville}</div>
                    </div>
                    
                    {/* Energy */}
                    <div className="text-center">
                      <div className="text-sm font-medium">{client.puissance_souscrite} kVA</div>
                      <div className="text-xs text-gray-500">Puissance</div>
                    </div>
                    
                    {/* Suppliers */}
                    <div>
                      <div className="text-sm">
                        {client.fournisseur_elec_actuel && (
                          <div className="text-gray-700">⚡ {client.fournisseur_elec_actuel}</div>
                        )}
                        {client.fournisseur_gaz_actuel && (
                          <div className="text-gray-700">🔥 {client.fournisseur_gaz_actuel}</div>
                        )}
                      </div>
                    </div>
                    
                    {/* Status & Commission */}
                    <div className="text-right">
                      <Badge className={getStatusColor(client.etat_dossier)}>
                        {getStatusLabel(client.etat_dossier)}
                      </Badge>
                      {client.commission && (
                        <div className="text-sm text-emerald-600 font-medium mt-1">
                          {client.commission}€
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="ml-4">
                    <Button 
                      onClick={() => handleViewClient(client)}
                      variant="outline"
                      size="sm"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Détails
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Compact View */}
      {viewMode === "compact" && (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          {sortedClients.map((client) => (
            <Card key={client.id} className="hover-lift cursor-pointer" data-testid={`client-card-${client.id}`}>
              <CardContent className="p-4">
                <div className="space-y-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-semibold text-sm text-gray-900 truncate">
                        {client.company_name}
                      </div>
                      <div className="text-xs text-gray-600 truncate">
                        {client.prenom} {client.nom}
                      </div>
                    </div>
                    <Badge className={`${getStatusColor(client.etat_dossier)} text-xs px-1 py-0`}>
                      {getStatusLabel(client.etat_dossier).substring(0, 3)}
                    </Badge>
                  </div>
                  
                  <div className="text-xs text-gray-500 space-y-1">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-3 h-3" />
                      <span>{formatDate(client.created_at)}</span>
                    </div>
                    {client.created_by_name && (
                      <div className="flex items-center space-x-1">
                        <User className="w-3 h-3" />
                        <span>par {client.created_by_name}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-1">
                      <Zap className="w-3 h-3" />
                      <span>{client.puissance_souscrite} kVA</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{client.ville}</span>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={() => handleViewClient(client)}
                    className="w-full"
                    variant="outline"
                    size="sm"
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    Voir
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Client Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Détails du Client</DialogTitle>
          </DialogHeader>
          
          {selectedClient && (
            <ClientDetails
              client={selectedClient}
              onClose={() => setShowDetailsDialog(false)}
              onUpdate={fetchClients}
              onDelete={fetchClients}
            />
          )}
        </DialogContent>
      </Dialog>

      {sortedClients.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun client trouvé</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm ? "Aucun client ne correspond à votre recherche" : "Commencez par ajouter votre premier client"}
          </p>
          {!searchTerm && (
            <Button 
              onClick={() => setShowAddDialog(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un client
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientsPage;